/**
 * TeamFollowupsPage — /team-followups
 *
 * Open leads whose follow-up is due today or overdue, across the whole
 * sales team, newest due date first, with each lead's owner. Filter by
 * owner. The daily 10:00 IST team WhatsApp summary links here.
 *
 * Access: salespeople (staff, creator, manager), admins and anyone who
 * sees the whole lead register — checked by GET /api/leads/team-followups.
 * "Open" goes to My List for your own leads and to the Lead Register for
 * people who can see every lead.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowRight, CalendarClock, Loader2, MapPin, Phone, RefreshCw, Users } from 'lucide-react';
import { api, useAuth } from '../lib/store';
import { logger } from '../lib/logger';

function fmtDay(ymd) {
  if (!ymd) return '—';
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function DueBadge({ days }) {
  if (!days) {
    return (
      <span className="inline-flex shrink-0 items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-gold">
        Due today
      </span>
    );
  }
  return (
    <span className="inline-flex shrink-0 items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700">
      {days} day{days === 1 ? '' : 's'} overdue
    </span>
  );
}

export default function TeamFollowupsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ leads: [], owners: [], due_today: 0, overdue: 0, today: '' });
  const [loading, setLoading] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getTeamFollowups();
      setData(res.data || {});
    } catch (err) {
      logger.error('Failed to load team follow-ups:', err);
      toast.error(err.response?.data?.detail || 'Failed to load team follow-ups');
      // No access (e.g. a dispatcher typing the address): back to the home
      // page instead of an empty list that looks like "nothing due".
      if (err.response?.status === 403) navigate('/', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => { if (!cancelled) fetchData(); });
    return () => { cancelled = true; };
  }, [fetchData]);

  const leads = useMemo(() => data.leads || [], [data.leads]);
  const visible = useMemo(
    () => (ownerFilter === 'all' ? leads : leads.filter((l) => l.owner_id === ownerFilter)),
    [leads, ownerFilter],
  );
  const canSeeAll = can('leads.view_all');
  const openLead = (l) => {
    if (l.is_mine) navigate(`/my-registrations?tab=leads&lead=${encodeURIComponent(l.id)}`);
    else if (canSeeAll) navigate(`/admin?tab=leads&lead=${encodeURIComponent(l.id)}`);
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="team-followups-page">
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 font-display text-[17px] font-bold text-heda-ink">
              <Users className="h-4 w-4 text-heda-gold" />
              Team follow-ups
            </CardTitle>
            <p className="mt-1 text-[12px] text-heda-mute" data-testid="team-followups-counts">
              {fmtDay(data.today)} · <strong className="text-heda-ink">{data.due_today || 0}</strong> due today ·{' '}
              <strong className="text-red-700">{data.overdue || 0}</strong> overdue
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            className="h-9 rounded-xl border-heda-line bg-white text-heda-ink hover:border-heda-gold hover:bg-white hover:text-heda-gold"
            aria-label="Refresh"
            data-testid="team-followups-refresh"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-1.5" data-testid="team-followups-owner-filter">
            {[{ id: 'all', name: 'Everyone', count: leads.length }, ...(data.owners || [])].map((o) => {
              const on = ownerFilter === o.id;
              return (
                <button
                  key={o.id || 'none'}
                  type="button"
                  onClick={() => setOwnerFilter(o.id)}
                  aria-pressed={on}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                    on ? 'border-heda-ink bg-heda-ink text-white' : 'border-heda-line bg-white text-heda-sub hover:border-heda-mute'
                  }`}
                  data-testid={`team-followups-owner-${o.id || 'none'}`}
                >
                  {o.name || '—'} <span className={`text-[10px] tabular-nums ${on ? 'text-white/70' : 'text-heda-mute'}`}>{o.count}</span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {loading && leads.length === 0 && (
        <p className="py-10 text-center text-sm text-heda-mute"><Loader2 className="mr-2 inline h-4 w-4 animate-spin text-heda-gold" />Loading…</p>
      )}
      {!loading && visible.length === 0 && (
        <p className="py-10 text-center text-sm text-heda-sub" data-testid="team-followups-empty">
          No follow-ups due today or overdue.
        </p>
      )}

      <div className="space-y-2">
        {visible.map((l) => (
          <Card key={l.id} className="rounded-2xl border-heda-line bg-white p-3 shadow-none sm:p-4" data-testid={`team-followup-${l.id}`}>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-bold text-heda-ink">{l.customer_name || '—'}</p>
                <p className="text-[12px] text-heda-sub" data-testid={`team-followup-owner-${l.id}`}>
                  Owner: <span className="font-semibold text-heda-ink">{l.owner_name || '—'}</span>
                  {l.is_mine && <span className="ml-1 rounded-full bg-heda-cream px-1.5 text-[10px] font-bold text-heda-mute">You</span>}
                </p>
              </div>
              <DueBadge days={l.days_overdue} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-heda-sub">
              {l.customer_phone ? (
                <a href={`tel:${l.customer_phone}`} className="inline-flex items-center gap-1 tabular-nums hover:text-heda-gold">
                  <Phone className="h-3 w-3" /> {l.customer_phone}
                </a>
              ) : (
                <span className="inline-flex items-center gap-1 text-heda-mute"><Phone className="h-3 w-3" /> no phone</span>
              )}
              {l.location && (
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {l.location}</span>
              )}
              <span className="inline-flex items-center gap-1 tabular-nums">
                <CalendarClock className="h-3 w-3" /> {fmtDay(l.follow_up_date)}
              </span>
              {(l.is_mine || canSeeAll) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openLead(l)}
                  className="ml-auto h-8 rounded-xl border-heda-line bg-white text-xs text-heda-ink hover:border-heda-gold hover:bg-white hover:text-heda-gold"
                  data-testid={`team-followup-open-${l.id}`}
                >
                  Open <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
